import { Pipe, PipeTransform } from '@angular/core';

/**
 * Angular pipe for interpolating parameters into template strings.
 *
 * This pipe is designed to handle parameter substitution in already-translated strings
 * that contain placeholder tokens in the format `{{ parameterName }}`.
 *
 * @example
 * Basic usage in templates:
 * ```html
 * <!-- Template string: "Hello {{ name }}, you have {{ count }} messages." -->
 * <div [innerHTML]="translatedText | interpolate : { name: 'John', count: 5 }"></div>
 * <!-- Result: "Hello John, you have 5 messages." -->
 * ```
 *
 * @example
 * Usage with HTML content:
 * ```html
 * <!-- Template: "<span class='bold'>{{ count }}</span> items found" -->
 * <div [innerHTML]="template | interpolate : { count: 42 }"></div>
 * <!-- Result: "<span class='bold'>42</span> items found" -->
 * ```
 *
 * @example
 * Multiple occurrences of same parameter:
 * ```html
 * <!-- Template: "{{ count }} items, {{ count }} total" -->
 * <div>{{ template | interpolate : { count: 10 } }}</div>
 * <!-- Result: "10 items, 10 total" -->
 * ```
 *
 * @since 1.0.0
 */
@Pipe({
  name: 'interpolate',
})
export class InterpolatePipe implements PipeTransform {
  /**
   * Transforms a template string by replacing parameter placeholders with actual values.
   *
   * The method searches for placeholders in the format `{{ parameterName }}` and replaces
   * them with corresponding values from the params object. If a parameter is missing
   * or has a null/undefined value, the placeholder is replaced with an empty string.
   *
   * @param value - The template string containing parameter placeholders
   * @param params - Object containing parameter key-value pairs for interpolation
   *
   * @returns The interpolated string with all placeholders replaced by their values
   *
   * @example
   * ```typescript
   * const pipe = new InterpolatePipe();
   * const result = pipe.transform('Hello {{ name }}!', { name: 'World' });
   * console.log(result); // "Hello World!"
   * ```
   *
   * @example
   * ```typescript
   * // Handling missing parameters
   * const result = pipe.transform('{{ greeting }} {{ name }}!', { greeting: 'Hello' });
   * console.log(result); // "Hello !"
   * ```
   *
   * @example
   * ```typescript
   * // With HTML content
   * const template = '<strong>{{ count }}</strong> results found';
   * const result = pipe.transform(template, { count: 25 });
   * console.log(result); // "<strong>25</strong> results found"
   * ```
   */
  transform(value: string, params?: { [key: string]: any } | null): string {
    if (!value) {
      return value;
    }

    if (!params) {
      return value;
    }

    let result = value;

    // Find all placeholders in the template using regex
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/g;
    let match;

    // Replace each placeholder with its corresponding value or empty string
    while ((match = placeholderRegex.exec(value)) !== null) {
      const fullPlaceholder = match[0]; // e.g., "{{ count }}"
      const paramKey = match[1]; // e.g., "count"
      const paramValue = params[paramKey];
      
      // Replace all occurrences of this placeholder
      const replacementValue = paramValue?.toString() || '';
      result = result.replace(new RegExp(fullPlaceholder.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), replacementValue);
    }

    return result;
  }
}
